import {FC, useState, useEffect, useMemo} from 'react'
import {Button} from '_core'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import Card from '_core/components/Card'
import {RootState} from 'setup'
import {ShippingDefinitionSchema} from 'app/features/orders/models'
import {
  setShippingLabelType,
  setShippingDate,
  setShippingLabelImageType,
  selectFulfillmentOrderItems,
  createCarrierFulfillment,
} from 'app/features/orders/redux/fulfillment/orderSlices'
import {labelImageTypes} from 'app/features/orders/redux/labelTypes'
import filter from 'lodash/filter'
import LabelValidationMessageDialog from '../LabelValidationMessageDialog'
import trim from 'lodash/trim'

type Props = {}

const Summary: FC<Props> = () => {
  const dispatch = useDispatch()
  const history = useNavigate()
  const [errors, setErrors] = useState<any[]>([])
  const shippingDates = useSelector<RootState>(
    ({orders}) => orders.fulfillment.order.shippingDates
  ) as any[]

  const orderItems = useSelector<RootState, any[]>(selectFulfillmentOrderItems)
  const order = useSelector<RootState>(({orders}) => orders.fulfillment.order) as any

  const shippingLabelImageTypes = useSelector<RootState>(
    ({orders}) => orders.shippingService.shippingLabel.labelImageTypes
  ) as any[]

  const shippingLabelTypes = useSelector<RootState>(
    ({orders}) => orders.shippingService.shippingLabel.labelTypes
  ) as any[]

  const shippingThermalLabelTypes = useSelector<RootState>(
    ({orders}) => orders.shippingService.shippingLabel.thermalLabelTypes
  ) as any[]

  const {shippingDefinition, isSubmitting} = order

  useEffect(() => {
    const validate = async () => {
      try {
        await ShippingDefinitionSchema.validate(shippingDefinition, {abortEarly: false})
        setErrors([])
      } catch (err: any) {
        setErrors(err.errors)
      }
    }

    validate()
  }, [shippingDefinition])

  const handleShippingDateChange = (e: any) => {
    const {value} = e.target
    dispatch(setShippingDate(value))
  }

  const handleImageTypeChange = (e: any) => {
    const {value} = e.target
    dispatch(setShippingLabelImageType(value))
  }

  const handleLabelTypeChange = (e: any) => {
    const {value} = e.target
    dispatch(setShippingLabelType(value))
  }

  const handelBuyLabel = async () => {
    if (await validate()) {
      dispatch(createCarrierFulfillment({order, history}))
    }
  }

  const validate = async () => {
    try {
      await ShippingDefinitionSchema.validate(shippingDefinition, {abortEarly: false})
      setErrors([])
      return true
    } catch (err: any) {
      setErrors(err.errors)
      return false
    }
  }

  const isValid = useMemo(() => {
    const filterItems = filter(orderItems, ({fulfillmentQuantity}) => fulfillmentQuantity > 0)
    return filterItems.length > 0
  }, [orderItems])

  const isPhoneNumberHave = useMemo(() => {
    let shippingPhone = order.order.shippingAddress?.phone
    if (trim(shippingPhone) === "") {
      shippingPhone = null
    }
    let customerPhone = order.order.customer?.phone
    if (trim(customerPhone) === "") {
      customerPhone = null
    }
    return shippingPhone || customerPhone
  }, [order.order.shippingAddress?.phone, order.order.customer?.phone])

  const getErrors = useMemo(() => {
    const errs: any[] = [...errors]
    if (!isPhoneNumberHave) {
      errs.push('Please add the phone number in the shipping address')
    }
    if (!isValid) {
      errs.push('Please update the item minimum 1 quantity for fulfillment')
    }
    return errs
  }, [errors, isValid, isPhoneNumberHave])

  return (
    <Card title='Summary' className='card-xl-stretch my-10 mb-xl-8 pb-10'>
      <div>
        <div className='mb-10 fv-row fv-plugins-icon-container'>
          <label className='required form-label'>Shipping date</label>
          <select
            title='Shipping Date'
            className='form-select'
            onChange={handleShippingDateChange}
            value={order.fulfillAt}
          >
            {shippingDates.map((shippingDate, index) => (
              <option key={index} value={shippingDate.date}>
                {shippingDate.label}
              </option>
            ))}
          </select>
        </div>

        <div className='mb-10 fv-row fv-plugins-icon-container'>
          <label className='required form-label'>Image Type</label>
          <select
            title='Shipping label'
            className='form-select'
            onChange={handleImageTypeChange}
            value={shippingDefinition.imageType}
          >
            <option value=''>Select</option>
            {shippingLabelImageTypes.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className='mb-10 fv-row fv-plugins-icon-container'>
          <label className='required form-label'>Label size</label>
          <select
            title='Shipping label'
            className='form-select'
            onChange={handleLabelTypeChange}
            value={shippingDefinition.labelType}
          >
            <option value=''>Select</option>
            {shippingLabelTypes.map((label, index) => (
              <option key={index} value={label.id}>
                {label.name}
              </option>
            ))}

            {shippingDefinition.imageType === labelImageTypes.zpl && (
              <>
                {shippingThermalLabelTypes.map((label, index) => (
                  <option key={index} value={label.id}>
                    {label.name}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>

        {/* <label className='form-check form-check-custom form-check-solid'>
          <input className='form-check-input' type='checkbox' />
          <span className='form-check-label'>Send shipping information to customers now.</span>
        </label> */}

        <div className='separator my-5 mx-n10' />

        <div className='timeline-content fw-normal text-muted ps-3'>
          In buying a shipping label, you agree to the terms of service. (opens a new window)
        </div>

        {getErrors.length ? (
          <div className='ps-3 my-3'>
            <div className='alert alert-dismissible bg-light-danger border border-danger border-dashed d-flex flex-column flex-sm-row w-100 p-5 my-5'>
              <ul className='d-flex flex-column'>
                {getErrors.map((error, index) => (
                  <li key={index} className='py-1 '>
                    <span className='text-danger'> {error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        <LabelValidationMessageDialog />

        <div className='mt-5 d-flex align-align-items-baseline justify-content-center'>
          <Button
            id='btnBuyLabel'
            className='btn btn-sm btn-primary px-10'
            isSubmitting={isSubmitting}
            title='Buy shipping label'
            onClick={handelBuyLabel}
            disabled={!isValid || getErrors.length > 0}
          />
        </div>
      </div>
    </Card>
  )
}

export default Summary
